import {ERecommendationsTypes, IRecommendationsResponse} from '../IRecommendationsMgr';
import RecommendationsProvider from './RecommendationsProvider';
import EinsteinAPI from 'commerceAPI/einstein';
import ProductModel from 'coreClasses/product/ProductModel';
import ManagerRepository from 'coreClasses/ManagerRepository';

/**
 * Represents EinsteinRecommendationsProvider
 */
class EinsteinRecommendationsProvider extends RecommendationsProvider {
	private einsteinApi: EinsteinAPI;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository, 'einstein');
		this.einsteinApi = new EinsteinAPI(this.managerRepository.getCommerceApi());
	}

	/**
	 * Returns recommendations
	 * @param {ERecommendationsTypes} type - recommendation type
	 * @param {ProductModel} product - product model
	 * @param {boolean} removeCurrentProduct - remove from response the passed product
	 * @return {IRecommendationsResponse} - recommendations
	 */
	getRecommendations(
		type: ERecommendationsTypes,
		products: ProductModel[],
		removeCurrentProduct: boolean = false
	): Promise<IRecommendationsResponse> {
		let body = {};
		const needsProducts =
			type === ERecommendationsTypes.YOU_MAY_ALSO_LIKE ||
			type === ERecommendationsTypes.COMPLETE_THE_SET ||
			type === ERecommendationsTypes.PRODUCT_TO_PRODUCT_THREE;

		if (needsProducts) {
			const productsPayload = (products || []).map((product) => ({id: (product as any).id}));

			body = {products: productsPayload};
		}

		return this.einsteinApi.getRecommendations(type, body).then((response) => {
			const productIds = response?.recs?.map((recommendation) => recommendation.id) || [];
			let productIdsResult = productIds;

			if (removeCurrentProduct) {
				const productsIdsParameter = (products || []).map((product) => (product as any).id);

				productIdsResult = productIds.filter((id) => !productsIdsParameter.includes(id));
			}

			return {
				__recoUUID: response.recoUUID,
				recommenderName: type,
				productIds: productIdsResult
			} as IRecommendationsResponse;
		});
	}

	/**
	 * Sends a view product event to einstein
	 * @param {ProductModel} product - product
	 */
	sendViewProduct(product: ProductModel): void {
		this.einsteinApi.sendViewProduct(product);
	}

	/**
	 * Sends a view recommendation event to einstein
	 * @param {IRecommendationsResponse} recommenderDetails - recommendations details
	 * @param {any} args - arguments
	 */
	sendViewReco(recommenderDetails: IRecommendationsResponse, args: any): void {
		this.einsteinApi.sendViewReco({
			__recoUUID: recommenderDetails.uuid,
			recommenderName: recommenderDetails.recommenderName,
			products: recommenderDetails.productIds.map((id) => ({id: id}))
		}, args);
	}

	/**
	 * Sends a click recommendation event to einstein
	 * @param {IRecommendationsResponse} recommenderDetails - recommendations details
	 * @param {ProductModel} product - product
	 * @param {any} args - arguments
	 */
	sendClickReco(recommenderDetails: IRecommendationsResponse, product: ProductModel, args: any): void {
		this.einsteinApi.sendClickReco(recommenderDetails, product, args);
	}

	/**
	 * Sends a add to cart event to einstein
	 * @param {object} productItem - product
	 * @param {ProductModel} product - product
	 * @param {any} args - arguments
	 */
	sendAddToCart(productItem: object, product: ProductModel, args: object = {}): void {
		this.einsteinApi.sendAddToCart(productItem, args);
	}

	/**
	 * Tells the Einstein engine when a user views search results.
	 * @param {string} searchText - search query
	 * @param {object[]} searchResults - products search results
	 * @param {any} args - arguments
	 */
	sendViewSearch(searchText: string, searchResults: object[], args: any): void {
		this.einsteinApi.sendViewSearch(searchText, searchResults, args);
	}

	/**
	 * Sends a category view
	 * @param {string} category - search query
	 * @param {object[]} searchResults - products search results
	 * @param {any} args - arguments
	 */
	sendViewCategory(category: object, searchResults: object[], args?: any): void {
		this.einsteinApi.sendViewCategory(category, searchResults, args);
	};

	/**
	 * Sends a click event for product in category
	 * @param {object} category - search query
	 * @param {ProductModel} product - products search results
	 * @param {any} args - arguments
	 */
	sendClickCategory(category: object, product: ProductModel, args?: any): void {
		this.einsteinApi.sendClickCategory(category, product, args);
	};

	/**
	 * Sends a click event for product in search
	 * @param {string} searchText - search query
	 * @param {ProductModel} product - products search results
	 * @param {any} args - arguments
	 */
	sendClickSearch(searchText: string, product: ProductModel, args?: any): void {
		this.einsteinApi.sendClickSearch(searchText, product, args);
	};

	/**
	 * Sends a page view event
	 * @param {string} path - current url
	 * @param {any} args - arguments
	 */
	sendViewPage(path: string, args?: any): void {
		this.einsteinApi.sendViewPage(path, args);
	};
}

export default EinsteinRecommendationsProvider;
